<template>
  <v-card class="mx-auto text-left">
    <v-container>
      <v-row>
        <v-col cols="12" class="py-0">
          <a :href="conceptLink" target="_blank" rel="noopener" @click.exact.prevent="navigateToConcept"
            :class="['primary--text', 'font-weight-bold', 'ma-0', 'text-decoration-none', 'cursor-pointer', { 'font-italic': concept.individual }]">
            {{ conceptTitle }}
            <span v-if="concept.terminologyLabel"> ({{ concept.terminologyLabel }})</span>
          </a>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="py-0">
          <p class="ftz-16 font-weight-light ma-0">{{ concept.id }}</p>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="py-0">
          <p class="ftz-16 font-weight-light ma-0">{{ getSkosNotationAndDcType(concept) }}</p>
        </v-col>
      </v-row>
      <v-row v-if="conceptDefinition">
        <v-col cols="12" class="py-0">
          <div class="ftz-14 font-weight-light ma-0 opacity-60">{{ conceptDefinitionEllipsis }}</div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="py-0">
          <!-- Pour mettre les autres liens plus tard... -->
        </v-col>
        <v-col cols="auto" class="py-0">
          <button class="btn btn--primary btn--link btn-xs"
            v-if="conceptDefinition && showDefinitionEllipsis && !definitionFull" @click="definitionMore">plus</button>
          <button class="btn btn--primary btn--link btn-xs"
            v-if="conceptDefinition && showDefinitionEllipsis && definitionFull" @click="definitionLess">moins</button>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import StoreConfiguration from '../store-configuration';


export default {
  name: "ConceptResult",

  data() {
    return {
      definitionFull: false,
      conceptTitle: undefined,
      conceptDefinition: undefined,
      definitionMaxLength: 330,
      storeConfiguration: StoreConfiguration.state,
      languageOrder: []
    }
  },

  props: {
    concept: Object,
    targetLang: String
  },

  async mounted() {
    if (this.targetLang == undefined) {
      await StoreConfiguration.getDefaultConfiguration();
      await StoreConfiguration.getConceptConfiguration(this.concept.terminologyName);
      let config = StoreConfiguration.getCurrentConceptConfiguration(null, this.concept.id);
      this.languageOrder = config.concepts_details.affichage.ordre_langues;
    }
    this.computeLanguageData();
  },

  watch: {
    concept() {
      this.definitionFull = false;
      this.computeLanguageData();
    },
  },

  computed: {
    conceptDefinitionEllipsis() {
      if (this.showDefinitionEllipsis && !this.definitionFull) {
        return this.conceptDefinition.substr(0, this.definitionMaxLength) + '...';
      }
      return this.conceptDefinition;
    },
    conceptLink() {
      const conceptsCatalogSlug = this.$searchResultManager?.conceptsCatalogSlug || 'explorer-les-concepts';
      const params = new URLSearchParams({
        lang: this.$route?.query?.lang || 'fr',
        concept: this.concept.id,
        treeViewType: 'simple'
      });

      return `/${conceptsCatalogSlug}/${encodeURIComponent(this.concept.terminologyName)}?${params.toString()}`;
    },
    showDefinitionEllipsis() {
      return this.conceptDefinition.length > (this.definitionMaxLength + 20);
    },
  },

  methods: {

    /**
     * Génère l'ordre final des langues pour la recherche des labels
     * Met la langue cible en première position si elle est définie
     * @param {Array} labelTraductionInOther - Tableau des langues disponibles en fallback
     * @returns {Array} - Tableau ordonné des langues à utiliser
     */
    generateLanguageFinalOrder(labelTraductionInOther) {
      let languageFinalOrder = this.languageOrder ? ['en', 'fr', ''] : labelTraductionInOther;

      // Si targetLang est défini, le mettre en première position
      if (this.targetLang && !languageFinalOrder.includes(this.targetLang)) {
        languageFinalOrder = [this.targetLang, ...languageFinalOrder];
      } else if (this.targetLang && languageFinalOrder.includes(this.targetLang)) {
        // Retirer targetLang de sa position actuelle et le mettre en premier
        languageFinalOrder = [this.targetLang, ...languageFinalOrder.filter(lang => lang !== this.targetLang)];
      }

      return languageFinalOrder;
    },

    /**
     * Calcule et assigne le titre et la définition du concept en fonction de la langue
     * Gère deux cas d'usage : page de déréférencement et résultats de recherche
     */
    computeLanguageData() {
      this.conceptTitle = undefined;
      this.conceptDefinition = undefined;

      let labelTraductionInOther = [];

      // Ajouter les traductions des labels présents dans "others"
      this.concept.others.forEach((item => {
        if (item.key == 'http://www.w3.org/2000/01/rdf-schema#label') {
          labelTraductionInOther.push(item.lang);
        }
      }))
      // Mettre en premire position le français, puis l'anglais, puis les autres langues présentes dans "others"
      labelTraductionInOther.unshift('en');
      labelTraductionInOther.unshift('fr');

      if (this.targetLang == undefined && this.languageOrder) {
        // Cas particulier où les vignettes sont utilisées pour la page de déréférencement

        // Transformer this.languageOrder en tableau
        let languageOrderArray = Object.values(this.languageOrder);

        let rdfsLabel = this.findRdfsLabel(languageOrderArray);
        if (rdfsLabel) {
          this.conceptTitle = rdfsLabel.value[0];
        } else {
          rdfsLabel = this.findRdfsLabel(labelTraductionInOther);
          if (rdfsLabel) {
            this.conceptTitle = rdfsLabel.value[0];
          }
        }
        if (this.conceptTitle == undefined) {
          this.conceptTitle = StoreConfiguration.extractUriLastPart(this.concept.id);
        }

        // Création de la définition - Doit être dans la même langue que le label sinon, ne rien afficher
        let languageOfrdfsLabel = rdfsLabel.lang;
        let skosDefinition = this.findExactOther('http://www.w3.org/2004/02/skos/core#definition', languageOfrdfsLabel);
        if (skosDefinition) {
          this.conceptDefinition = skosDefinition.value[0];
        }
      } else {
        // Cas où les vignettes sont utilisées pour les résultats de recherche (cas historique)
        let languageFinalOrder = this.generateLanguageFinalOrder(labelTraductionInOther);
        let rdfsLabel = this.findRdfsLabel(languageFinalOrder);
        let labelLang = '';
        if (rdfsLabel) {
          this.conceptTitle = rdfsLabel.value[0];
          labelLang = rdfsLabel.lang || '';
        } else if (this.concept.prefLabel != null) {
          this.conceptTitle = this.concept.prefLabel;
        } else {
          this.conceptTitle = StoreConfiguration.extractUriLastPart(this.concept.id);
        }

        let skosDefinition = this.findExactOther('http://www.w3.org/2004/02/skos/core#definition', labelLang);
        if (skosDefinition) {
          this.conceptDefinition = skosDefinition.value[0];
        }
      }
    },

    /**
     * Recherche le label RDFS dans les langues spécifiées par ordre de priorité
     * @param {Array} langs - Tableau des codes de langue à rechercher (ex: ['fr', 'en', ''])
     * @returns {Object|undefined} - L'objet contenant le label trouvé ou undefined
     */
    findRdfsLabel(langs) {
      let foundLabel;
      if (this.concept.others) {
        langs.forEach(lang => {
          if (!foundLabel) {
            foundLabel = this.findExactOther('http://www.w3.org/2000/01/rdf-schema#label', lang);
          }
        });
      }
      return foundLabel
    },

    /**
     * Recherche une propriété spécifique dans une langue donnée parmi les attributs "others" du concept
     * @param {string} uri - L'URI de la propriété recherchée
     * @param {string} lang - Le code de la langue recherchée (peut être vide '')
     * @returns {Object|undefined} - L'objet trouvé ou undefined
     */
    findExactOther(uri, lang) {
      return this.concept.others.find(other => {
        return other.key === uri
          && (
            (lang == '' && (!other.lang || other.lang == ''))
            ||
            (lang != '' && other.lang === lang)
          )
      });
    },

    /**
     * Construit une chaîne affichant la notation SKOS et le type DC du concept
     * @param {Object} concept - L'objet concept
     * @returns {string} - Chaîne formatée "code - type" ou seulement l'un des deux
     */
    getSkosNotationAndDcType(concept) {
      let vars = [];
      if (concept.code) {
        vars.push(concept.code);
      }
      if (concept.dcType && concept.dcType !== 'Autre') {
        vars.push(concept.dcType);
      }
      return vars.join(' - ');
    },

    /**
     * Affiche la définition complète du concept
     */
    definitionMore() {
      this.definitionFull = true;
    },

    /**
     * Réduit la définition du concept (affichage avec ellipse)
     */
    definitionLess() {
      this.definitionFull = false;
    },

    /**
     * Navigue vers la page de détail du concept en utilisant Vue Router
     * Cela évite le rechargement complet de la page
     */
    navigateToConcept() {
      const conceptsCatalogSlug = this.$searchResultManager?.conceptsCatalogSlug || 'explorer-les-concepts';
      const params = {
        lang: this.$route.query.lang || 'fr',
        concept: this.concept.id,
        treeViewType: 'simple'
      };

      this.$router.push({
        path: `/${conceptsCatalogSlug}/${encodeURIComponent(this.concept.terminologyName)}`,
        query: params
      }).catch(() => {
        // Ignore les erreurs de navigation redondante
      });
    },

  }
}
</script>

<style scoped>
.ftz-14 {
  font-size: 14px;
}

.opacity-60 {
  opacity: 0.6;
}

.cursor-pointer {
  cursor: pointer;
}

.v-sheet.v-card {
  border-radius: 1.4em;
  box-shadow: none;
  border: .1rem solid #dee0ea;
  color: #697097;
}

.v-sheet.v-card .container {
  padding: 2rem;
}
</style>